// Touchscreen controls for mobile devices with proper multi-touch support
import { WEAPON_Y_OFFSET } from "../models/player";
import { drawAttackField } from "../ui/attack-field";
import { drawFullThrowablePreview } from "../handlers/throwable-handler";
import { isTouchDevice } from "../utils/mobile-utils";
import { spawnPlayerLabel } from "../ui/damage-text";
import { playSound } from "../utils/sound-utils";
import { SprintButton } from "../ui/sprint-button";
import { StaminaMeter } from "../ui/stamina-meter";
import { EmojiPicker } from "../ui/emoji-picker";
import { calcMoveStep, clampToMapBounds } from "../utils/movement-utils";
import {
  TILE_SIZE,
  PLAYER_SPRINT_MULTIPLIER,
  PLAYER_UNARMED_ATTACK_RANGE,
  PLAYER_UNARMED_ATTACK_INTERVAL,
  Item,
} from "../models/common";

// Global references to current joysticks for external control
let globalMovementJoystick: PhaserJoystick | null = null;
let globalAttackJoystick: PhaserJoystick | null = null;
let globalSprintButton: SprintButton | null = null;
let globalEmojiButton: Phaser.GameObjects.Image | null = null;
let currentEmojiPicker: EmojiPicker | null = null;
let globalLocalOnly: boolean = false;



class PhaserJoystick {
  public dx = 0;
  public dy = 0;
  private active = false;
  private joystickBase: Phaser.GameObjects.Graphics;
  private joystickKnob: Phaser.GameObjects.Graphics;
  private readonly maxDist = 40;
  private activePointer: Phaser.Input.Pointer | null = null;
  private onMoveCallback: (dx: number, dy: number) => void;
  private onStartCallback?: () => void;
  private onEndCallback?: () => void;
  private scene: Phaser.Scene;
  private baseX: number;
  private baseY: number;
  private initialBaseX: number; // Store the original position
  private initialBaseY: number;
  private _isThrowableMode = false;
  public get isThrowableMode() {
    return this._isThrowableMode;
  }
  private blinkTween: Phaser.Tweens.Tween | null = null;
  private distanceIndicator: Phaser.GameObjects.Graphics | null = null;
  private uiLayer: Phaser.GameObjects.Container | null = null;
  private interactiveArea: Phaser.GameObjects.Zone | null = null;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    onMoveCallback: (dx: number, dy: number) => void,
    onStartCallback?: () => void,
    onEndCallback?: () => void,
    uiLayer?: Phaser.GameObjects.Container,
  ) {
    this.scene = scene;
    this.baseX = x;
    this.baseY = y;
    this.initialBaseX = x; // Store original position
    this.initialBaseY = y;
    this.onMoveCallback = onMoveCallback;
    this.onStartCallback = onStartCallback;
    this.onEndCallback = onEndCallback;
    this.uiLayer = uiLayer || null;

    // Create joystick base (outer circle) - positioned relative to camera
    this.joystickBase = scene.add.graphics();
    this.joystickBase.setScrollFactor(0); // Fixed to camera
    this.joystickBase.setDepth(10000); // High depth to stay on top
    this.updateBaseGraphics(0.1, 0.1);

    // Create joystick knob (inner circle) - positioned relative to camera
    this.joystickKnob = scene.add.graphics();
    this.joystickKnob.setScrollFactor(0); // Fixed to camera
    this.joystickKnob.setDepth(10001);
    this.updateKnobGraphics(0.3, 0.05);

    // Position both graphics at the base position
    this.updatePositions();

    // Create larger invisible interactive area for touch detection
    // This allows more freedom of movement for the Brawl Stars-style following behavior
    const interactiveSize = this.maxDist * 4; // Larger area for better responsiveness
    this.interactiveArea = scene.add.zone(
      this.baseX,
      this.baseY,
      interactiveSize,
      interactiveSize,
    );
    this.interactiveArea.setScrollFactor(0);
    this.interactiveArea.setInteractive();
    this.interactiveArea.setDepth(10002);

    // Add to UI layer if provided
    if (this.uiLayer) {
      this.uiLayer.add(this.joystickBase);
      this.uiLayer.add(this.joystickKnob);
      this.uiLayer.add(this.interactiveArea);
    }

    this.setupEventListeners(this.interactiveArea);
  }

  private updateBaseGraphics(
    lineAlpha: number,
    fillAlpha: number,
    color: number = 0xffffff,
  ) {
    this.joystickBase.clear();
    // Draw a white outline (thicker than the normal stroke)
    this.joystickBase.lineStyle(2, 0xffffff, 0.2); // White, fully opaque
    this.joystickBase.strokeCircle(0, 0, this.maxDist + 1.5); // Slightly larger radius for outline

    // Draw the normal base graphics
    this.joystickBase.lineStyle(2, color, lineAlpha);
    this.joystickBase.fillStyle(0x000000, fillAlpha);
    this.joystickBase.strokeCircle(0, 0, this.maxDist);
    this.joystickBase.fillCircle(0, 0, this.maxDist);
  }

  private updateKnobGraphics(
    lineAlpha: number,
    fillAlpha: number,
    color: number = 0xffffff,
  ) {
    this.joystickKnob.clear();
    this.joystickKnob.lineStyle(1, 0x000000, lineAlpha);
    this.joystickKnob.fillStyle(color, fillAlpha);
    this.joystickKnob.strokeCircle(0, 0, 20);
    this.joystickKnob.fillCircle(0, 0, 20);
  }

  private updatePositions() {
    this.joystickBase.setPosition(this.baseX, this.baseY);
    this.joystickKnob.setPosition(this.baseX + this.dx, this.baseY + this.dy);
  }

  private setupEventListeners(interactiveArea: Phaser.GameObjects.Zone) {
    // Handle pointer down (touch start)
    interactiveArea.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      // Only handle if we don't already have an active pointer
      if (this.activePointer === null) {
        this.activePointer = pointer;
        this.active = true;

        // Update visual state to active
        this.updateBaseGraphics(0.15, 0.2);
        this.updateKnobGraphics(0.5, 0.1);

        if (this.onStartCallback) {
          this.onStartCallback();
        }
      }
    });

    // Handle pointer move (touch move)
    this.scene.input.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (this.active && this.activePointer === pointer) {
        // Calculate relative position from joystick center using screen coordinates
        let rawDx = pointer.x - this.baseX;
        let rawDy = pointer.y - this.baseY;

        const dist = Math.sqrt(rawDx * rawDx + rawDy * rawDy);

        // Brawl Stars behavior: if the touch goes beyond maxDist,
        // move the base to follow the knob
        if (dist > this.maxDist) {
          // Calculate how much to shift the base
          const angle = Math.atan2(rawDy, rawDx);
          const shiftDist = dist - this.maxDist;

          // Move the base in the direction of movement
          this.baseX += Math.cos(angle) * shiftDist;
          this.baseY += Math.sin(angle) * shiftDist;

          // Recalculate dx/dy from new base position
          rawDx = pointer.x - this.baseX;
          rawDy = pointer.y - this.baseY;

          // Clamp to maxDist (should be exactly at maxDist now)
          const newDist = Math.sqrt(rawDx * rawDx + rawDy * rawDy);
          this.dx = (rawDx / newDist) * this.maxDist;
          this.dy = (rawDy / newDist) * this.maxDist;
        } else {
          // Within bounds, use raw values
          this.dx = rawDx;
          this.dy = rawDy;
        }

        // Update knob position
        this.updatePositions();

        // Update distance indicator for throwables
        this.updateDistanceIndicator();

        this.onMoveCallback(this.dx, this.dy);
      }
    });

    // Handle pointer up (touch end)
    this.scene.input.on("pointerup", (pointer: Phaser.Input.Pointer) => {
      if (this.active && this.activePointer === pointer) {
        this.reset();
      }
    });
  }

  private reset() {
    this.active = false;
    this.activePointer = null;

    // Store throwable data if in throwable mode
    const wasThrowableMode = this.isThrowableMode;
    let throwData = null;
    if (wasThrowableMode && this.force > 5) {
      const player = (this.scene as any).player;
      const activeThrowableItem = (this.scene as any).activeThrowableItem;
      throwData = {
        itemDefId: activeThrowableItem
          ? activeThrowableItem.definition.id
          : undefined,
        startX: player ? player.x : undefined,
        startY: player ? player.y : undefined,
        distance: Math.min(this.force / this.maxDist, 1) * 200,
        direction:
          this.force > 0
            ? { x: this.dx / this.force, y: this.dy / this.force }
            : { x: 0, y: 0 },
      };
    }

    this.dx = 0;
    this.dy = 0;

    // Reset base to original position (Brawl Stars behavior)
    this.baseX = this.initialBaseX;
    this.baseY = this.initialBaseY;

    // Hide distance indicator
    if (this.distanceIndicator) {
      this.distanceIndicator.destroy();
      this.distanceIndicator = null;
    }

    // Reset visual state
    if (this.isThrowableMode) {
      this.updateBaseGraphics(0.15, 0.2, 0xffff00);
      this.updateKnobGraphics(0.5, 0.1, 0xffff00);
    } else {
      this.updateBaseGraphics(0.1, 0.1);
      this.updateKnobGraphics(0.3, 0.05);
    }

    // Reset knob position to center
    this.updatePositions();

    if (this.onEndCallback) {
      this.onEndCallback();
    }

    // Emit throwable release event if applicable
    if (throwData) {
      this.scene.events.emit("throwable-release", throwData);
    }
  }

  get force() {
    return Math.sqrt(this.dx * this.dx + this.dy * this.dy);
  }

  get isActive() {
    return this.active;
  }

  setVisible(visible: boolean) {
    // Safety check: ensure joystick components are initialized and scene is valid
    if (!this.joystickBase || !this.joystickKnob || !this.scene?.sys) {
      return;
    }
    this.joystickBase.setVisible(visible);
    this.joystickKnob.setVisible(visible);

    /*
    // Disable/enable interactions when joystick visibility changes
    if (this.interactiveArea) {
      if (visible) {
        this.interactiveArea.setInteractive();
      } else {
        this.interactiveArea.disableInteractive();
      }
    }
    */
  }

  setThrowableMode(enabled: boolean) {
    this._isThrowableMode = enabled;

    if (enabled) {
      // Start blinking with yellow color
      this.blinkTween = this.scene.tweens.add({
        targets: [this.joystickBase, this.joystickKnob],
        alpha: { from: 1, to: 0.3 },
        duration: 300,
        ease: "Power2",
        yoyo: true,
        repeat: -1,
      });
      this.updateBaseGraphics(0.15, 0.2, 0xffff00);
      this.updateKnobGraphics(0.5, 0.1, 0xffff00);
    } else {
      // Stop blinking and restore normal colors
      if (this.blinkTween) {
        this.blinkTween.stop();
        this.blinkTween = null;
      }
      if (this.distanceIndicator) {
        this.distanceIndicator.destroy();
        this.distanceIndicator = null;
      }
      this.joystickBase.alpha = 1;
      this.joystickKnob.alpha = 1;
      this.updateBaseGraphics(0.1, 0.1);
      this.updateKnobGraphics(0.3, 0.05);
    }
  }

  private updateDistanceIndicator() {
    if (!this.isThrowableMode || !this.active) return;

    if (!this.distanceIndicator) {
      this.distanceIndicator = this.scene.add.graphics();
      this.distanceIndicator.setScrollFactor(1); // Follow camera/world, not UI
      this.distanceIndicator.setDepth(9999);

      // Make sure UI camera ignores this graphics (game world element)
      if ((this.scene as any).uiCamera) {
        (this.scene as any).uiCamera.ignore(this.distanceIndicator);
      }
    }

    this.distanceIndicator.clear();

    if (this.force > 5) {
      // Get player position from the scene
      const player = (this.scene as any).player;
      if (!player) return;

      const playerX = player.x;
      const playerY = player.y;

      const distance = Math.min(this.force / this.maxDist, 1) * 200; // Max 200 pixels throw distance
      const angle = Math.atan2(this.dy, this.dx);
      const endX = playerX + Math.cos(angle) * distance;
      const endY = playerY + Math.sin(angle) * distance;

      drawFullThrowablePreview(
        this.distanceIndicator,
        playerX,
        playerY,
        endX,
        endY,
        {
          color: 0xffff00,
          arcHeight: 50,
          steps: 24,
          showTargetCircle: true,
          showMarkers: true,
          targetRadius: 15,
          markerCount: 3,
        },
      );
    }
  }

  destroy() {
    this.setThrowableMode(false);
    if (this.joystickBase) {
      this.joystickBase.destroy();
    }
    if (this.joystickKnob) {
      this.joystickKnob.destroy();
    }
    if (this.interactiveArea) {
      this.interactiveArea.destroy();
    }
  }
}

export function setupTouchControls(scene: any, localOnly: boolean = false, showSprint: boolean = true) {
  globalLocalOnly = localOnly;
  // Sprint is always active in the market scene and costs no stamina there
  const isMarketScene = scene.scene.key === "MarketScene";
  // Enable multi-touch support
  scene.input.addPointer(2); // Allow up to 3 total pointers (default 1 + 2 more)

  // Movement state
  let movementInterval: number | null = null;
  let isMoving = false;
  let currentMovement = { dx: 0, dy: 0 };

  // Attack state
  let isAttacking = false;
  let currentAttackDirection = { dx: 0, dy: 0 };
  let lastAttackTime = 0;
  let attackCheckInterval: number | null = null;
  let aimStartTime = 0; // Track when aiming started
  let isAiming = false; // Track if we're currently in the aiming phase
  let hasAimed = false; // Track if we've completed aiming for this attack sequence

  // Calculate joystick positions based on game viewport size
  // Use scene.scale dimensions which are the actual game size, not camera size
  const gameWidth = scene.scale.width;
  const gameHeight = scene.scale.height;
  const leftJoystickX = 120; // 60px radius + 70px margin
  const leftJoystickY = gameHeight - 80;
  const rightJoystickX = gameWidth - 120;
  const rightJoystickY = gameHeight - 120; // Moved up from gameHeight - 80

  // Get UI layer from scene if available
  const uiLayer = scene.uiLayer || undefined;

  // Sprint dust tracking
  let lastDustTileX = -1;
  let lastDustTileY = -1;

  // Movement joystick (left side)
  const movementJoystick = new PhaserJoystick(
    scene,
    leftJoystickX,
    leftJoystickY,
    (dx: number, dy: number) => {
      // Update movement direction
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 5) {
        currentMovement.dx = dx / dist;
        currentMovement.dy = dy / dist;
      } else {
        currentMovement.dx = 0;
        currentMovement.dy = 0;
      }
    },
    () => {
      // Start continuous movement
      isMoving = true;
      if (!movementInterval) {
        let lastIntervalTime = Date.now();
        movementInterval = window.setInterval(() => {
          const now = Date.now();
          const delta = now - lastIntervalTime;
          lastIntervalTime = now;
          if (isMoving && scene.player && scene.player.isAlive()) {
            const dist = movementJoystick.force;
            if (dist > 5) {
              if (localOnly) {
                // Move player locally with bounds checking
                // Scale by joystick distance for analog feel
                const joystickScale = Math.min(dist / 60, 1);
                // Apply sprint multiplier if sprinting and moving
                const sprintMultiplier =
                  isMarketScene || (isSprinting && globalSprintButton?.isPressed() && stamina > 0)
                    ? PLAYER_SPRINT_MULTIPLIER
                    : 1;
                const moveStep = calcMoveStep(delta, sprintMultiplier) * joystickScale;
                // Run the body/hat animation twice as fast while sprinting
                scene.player.setSprinting?.(sprintMultiplier > 1);
                const newX = scene.player.x + currentMovement.dx * moveStep;
                const newY = scene.player.y + currentMovement.dy * moveStep;
                const clampedPos = clampToMapBounds(newX, newY, scene);

                // Add offset to check position ahead when moving down
                // This prevents player from walking too close to blocked tiles
                const checkOffsetY = currentMovement.dy > 0 ? 16 : 0; // Check 12 pixels ahead when moving down
                const checkX = clampedPos.x;
                const checkY = clampedPos.y + checkOffsetY;

                if (!scene.isBlocked(checkX, checkY)) {
                  // Update sprite direction only if there's significant horizontal movement
                  // For touch/joystick, use normalized movement direction
                  if (Math.abs(currentMovement.dx) > 0.1) {
                    if (currentMovement.dx < 0) {
                      scene.player.setFacing(true);
                    } else if (currentMovement.dx > 0) {
                      scene.player.setFacing(false);
                    }
                  }
                  // Move instantly (sets both position and target to same value)
                  scene.player.x = clampedPos.x;
                  scene.player.y = clampedPos.y;
                  scene.player.targetX = clampedPos.x;
                  scene.player.targetY = clampedPos.y;
                  scene.player.container.setPosition(
                    clampedPos.x,
                    clampedPos.y,
                  );
                  // Trigger walking animation
                  scene.player.startMovingAnimation();
                  // Spawn dust particle if sprinting and on a new tile
                  if ((isMarketScene || isSprinting) && scene.player.spawnDustParticle) {
                    const tileX = Math.floor(clampedPos.x / TILE_SIZE);
                    const tileY = Math.floor(clampedPos.y / TILE_SIZE);
                    if (tileX !== lastDustTileX || tileY !== lastDustTileY) {
                      scene.player.spawnDustParticle();
                      lastDustTileX = tileX;
                      lastDustTileY = tileY;
                    }
                  }
                  // Trigger move callback to close windows
                  if (scene.player.onMoveCallback) {
                    scene.player.onMoveCallback();
                  }
                  if (scene.checkEffectTile(clampedPos.x, clampedPos.y)) {
                    // Stop all sounds from base scene before transition
                    scene.sound.stopAll();
                    // Play teleport sound
                    playSound(scene, "teleport");
                    isMoving = false;
                    if (movementInterval) {
                      clearInterval(movementInterval);
                      movementInterval = null;
                    }

                    // Check if player is in BaseScene at marketplace teleport (24, 9-10).
                    // Expand range by 1 tile in each direction to account for checkEffectTile's
                    // 12px offset radius, which can detect a tile when the player's center is
                    // in an adjacent tile.
                    const tileX = Math.floor(clampedPos.x / TILE_SIZE);
                    const tileY = Math.floor(clampedPos.y / TILE_SIZE);
                    const isMarketplaceTeleport = scene.scene.key === "BaseScene" && (tileX >= 23 && tileX <= 25) && (tileY >= 8 && tileY <= 11);

                    if (isMarketplaceTeleport) {
                      // Enter marketplace
                      scene.enterMarketplace();
                    } else {
                      // Check if player is in a squad
                      const squadCode = localStorage.getItem("squadCode");
                      if (squadCode) {
                        // Emit squad teleport event to server - it will coordinate all members
                        if (window.socket) {
                          window.socket.emit("squad-teleport");
                        }
                      } else {
                        // Solo player - show loadout screen then transition
                        scene.enterLobby();
                      }
                    }
                    // Squad members will receive squad-join-lobby event from server

                    return;
                  }
                }
              } else {
                // Client-side prediction: frame-independent movement
                // Apply sprint multiplier if sprinting and moving
                const sprintMultiplier =
                  isMarketScene || (isSprinting && globalSprintButton?.isPressed() && stamina > 0)
                    ? PLAYER_SPRINT_MULTIPLIER
                    : 1;
                const joystickScale = Math.min(dist / 60, 1);
                const moveStep = calcMoveStep(delta, sprintMultiplier) * joystickScale;
                // Run the body/hat animation twice as fast while sprinting
                scene.player.setSprinting?.(sprintMultiplier > 1);
                const newX = scene.player.x + currentMovement.dx * moveStep;
                const newY = scene.player.y + currentMovement.dy * moveStep;
                const clampedPos = clampToMapBounds(newX, newY, scene);

                // Add offset to check position ahead when moving down
                // This prevents player from walking too close to blocked tiles
                const checkOffsetY = currentMovement.dy > 0 ? 12 : 0; // Check 12 pixels ahead when moving down
                const checkX = clampedPos.x;
                const checkY = clampedPos.y + checkOffsetY;

                // Check if tile is blocked using local map data
                if (!scene.isBlocked(checkX, checkY)) {
                  // Calculate actual movement delta to determine direction
                  const actualDx = clampedPos.x - scene.player.x;
                  const actualDy = clampedPos.y - scene.player.y;

                  // Update sprite direction only if there's significant horizontal movement
                  if (Math.abs(actualDx) > 0.5) {
                    if (actualDx < 0) {
                      scene.player.setFacing(true);
                    } else {
                      scene.player.setFacing(false);
                    }
                  }

                  // Move instantly (sets both position and target to same value)
                  scene.player.x = clampedPos.x;
                  scene.player.y = clampedPos.y;
                  scene.player.targetX = clampedPos.x;
                  scene.player.targetY = clampedPos.y;
                  scene.player.container.setPosition(
                    clampedPos.x,
                    clampedPos.y,
                  );

                  // Update camera movement direction tracking
                  if (scene.updateMovementDirection) {
                    scene.updateMovementDirection(
                      -currentMovement.dx,
                      -currentMovement.dy,
                    );
                  }

                  // Trigger walking animation
                  scene.player.startMovingAnimation();
                  // Spawn dust particle if sprinting (with tile change check)
                  if ((isMarketScene || isSprinting) && scene.player.spawnDustParticle) {
                    const tileX = Math.floor(clampedPos.x / TILE_SIZE);
                    const tileY = Math.floor(clampedPos.y / TILE_SIZE);
                    if (tileX !== lastDustTileX || tileY !== lastDustTileY) {
                      scene.player.spawnDustParticle();
                      lastDustTileX = tileX;
                      lastDustTileY = tileY;
                    }
                  }

                  // Trigger move callback to close windows
                  if (scene.player.onMoveCallback) {
                    scene.player.onMoveCallback();
                  }
                }
              }
            }
          }
        }, 16); // ~60 FPS for smooth animation
      }
    },
    () => {
      // Stop movement
      isMoving = false;
      if (movementInterval) {
        clearInterval(movementInterval);
        movementInterval = null;
      }
      // Reset movement direction to trigger camera return to center
      if (scene.updateMovementDirection) {
        scene.updateMovementDirection(0, 0);
      }
    },
    uiLayer,
  );

  // Attack joystick (right side)
  let attackFieldGraphics: Phaser.GameObjects.Graphics | null = null;
  const attackJoystick = new PhaserJoystick(
    scene,
    rightJoystickX,
    rightJoystickY,
    (dx: number, dy: number) => {
      // If throwable mode, disable attack visuals and logic
      if (attackJoystick.isThrowableMode) {
        if (attackFieldGraphics) {
          attackFieldGraphics.destroy();
          attackFieldGraphics = null;
        }
        return;
      }
      // Normal attack logic
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 5) {
        currentAttackDirection.dx = dx / dist;
        currentAttackDirection.dy = dy / dist;
      }
      if (scene.player && scene.player.isAlive() && dist > 5) {
        // Rotate player's weapon in the attack direction
        if (scene.player.weaponSprite && scene.player.weaponSprite.visible) {
          scene.player.rotateWeapon({
            x: currentAttackDirection.dx,
            y: currentAttackDirection.dy,
          });
        }

        if (attackFieldGraphics) {
          attackFieldGraphics.destroy();
          attackFieldGraphics = null;
        }
        let range = 60;
        let accuracy = 100;
        let spreadAngle = undefined;
        const weapon = scene.player.data?.equipment?.Weapon;
        if (weapon && weapon.definition.stats) {
          if (weapon.definition.stats.range)
            range = weapon.definition.stats.range;
          if (weapon.definition.stats.accuracy !== undefined)
            accuracy = weapon.definition.stats.accuracy;
        } else {
          range = PLAYER_UNARMED_ATTACK_RANGE;
          accuracy = 100;
          spreadAngle = Math.PI / 2;
        }
        attackFieldGraphics = drawAttackField(
          scene,
          scene.player.x,
          scene.player.y + WEAPON_Y_OFFSET,
          { x: currentAttackDirection.dx, y: currentAttackDirection.dy },
          range,
          accuracy,
          0xff0000,
          0.1,
          spreadAngle,
        );
      } else {
        if (attackFieldGraphics) {
          attackFieldGraphics.destroy();
          attackFieldGraphics = null;
        }
      }
    },
    () => {
      // Attack start - hide sprint button
      globalSprintButton?.setVisible(false);

      // If throwable mode, do not start attack interval
      if (attackJoystick.isThrowableMode) {
        isAttacking = false;
        if (attackCheckInterval) {
          clearInterval(attackCheckInterval);
          attackCheckInterval = null;
        }
        return;
      }
      if (scene.player && scene.player.isAlive()) {
        isAttacking = true;

        // Get aim delay from equipped weapon
        let aimDelayMs = 0;
        let hasAmmo = true;
        if (
          window.player &&
          window.player.data &&
          window.player.data.equipment &&
          window.player.data.equipment.Weapon
        ) {
          const weapon: Item = window.player.data.equipment.Weapon;
          if (weapon.definition.stats && weapon.definition.stats.aimDelay) {
            aimDelayMs = weapon.definition.stats.aimDelay; // Convert seconds to milliseconds
          }
          // Check if weapon has ammo
          hasAmmo = weapon.currentAmmo === undefined || weapon.currentAmmo > 0;
        }

        // If weapon has aim delay, has ammo, and we haven't aimed yet in this sequence, start aiming
        if (aimDelayMs > 0 && hasAmmo && !hasAimed) {
          isAiming = true;
          aimStartTime = Date.now();
          scene.player.createAimCooldownIndicator(scene);
          // Show "HOLD TO AIM" status label
          spawnPlayerLabel(
            "HOLD TO SHOOT",
            scene.player.x,
            scene.player.y - 48,
            scene,
            "#ffdd00",
          );
        }

        if (!attackCheckInterval) {
          attackCheckInterval = window.setInterval(() => {
            if (!scene.player || !scene.player.isAlive()) {
              return;
            }
            let attackIntervalMs = PLAYER_UNARMED_ATTACK_INTERVAL;
            let aimDelayMs = 0;
            if (
              window.player &&
              window.player.data &&
              window.player.data.equipment &&
              window.player.data.equipment.Weapon
            ) {
              const weapon: Item = window.player.data.equipment.Weapon;
              if (
                weapon.definition.stats &&
                weapon.definition.stats.attackInterval
              ) {
                attackIntervalMs = weapon.definition.stats.attackInterval;
              }
              if (weapon.definition.stats && weapon.definition.stats.aimDelay) {
                aimDelayMs = weapon.definition.stats.aimDelay;
              }
            }

            const now = Date.now();

            // Check if we're still in the aiming phase
            if (isAiming) {
              const timeSinceAimStart = now - aimStartTime;
              if (timeSinceAimStart >= aimDelayMs) {
                // Aiming complete
                isAiming = false;
                hasAimed = true;
                // Allow first attack immediately after aiming completes
                lastAttackTime = now - attackIntervalMs;
              } else {
                // Still aiming, can't attack yet
                return;
              }
            }

            const timeSinceLastAttack = now - lastAttackTime;
            if (
              isAttacking &&
              attackJoystick.force > 5 &&
              timeSinceLastAttack >= attackIntervalMs
            ) {
              (window as any).socket.emit("player_attack", {
                attackerId: scene.player.socketId,
                x: scene.player.x,
                y: scene.player.y,
                direction: {
                  x: currentAttackDirection.dx,
                  y: currentAttackDirection.dy,
                },
              });
              lastAttackTime = now;
              scene.player.createAttackCooldownIndicator(scene);
            }
          }, 50);
        }
      }
    },
    () => {
      // Attack end - show sprint button again
      globalSprintButton?.setVisible(true);

      isAttacking = false;

      // If aiming was in progress, immediately destroy aim cooldown bars
      if (isAiming && scene.player && scene.player.aimCooldownBars) {
        scene.player.aimCooldownBars.forEach((bar: any) => {
          if (bar && typeof bar.destroy === "function") {
            bar.destroy();
          }
        });
        scene.player.aimCooldownBars = [];
      }

      isAiming = false;
      hasAimed = false;

      if (attackFieldGraphics) {
        attackFieldGraphics.destroy();
        attackFieldGraphics = null;
      }
    },
    uiLayer,
  );

  // --- Always update cooldown bar progress, even when not attacking ---
  // This ensures the cooldown bar animates smoothly while holding down attack
  scene.events.on("postupdate", () => {
    // Keep the attack field glued to the player's current position every frame.
    // The aim joystick's onMoveCallback only fires when the thumb itself moves,
    // so without this the field lags behind while walking and holding the aim
    // joystick steady.
    if (isAttacking && !attackJoystick.isThrowableMode && attackJoystick.force > 5) {
      if (attackFieldGraphics) {
        attackFieldGraphics.destroy();
        attackFieldGraphics = null;
      }
      if (scene.player && scene.player.isAlive()) {
        let range = 60;
        let accuracy = 100;
        let spreadAngle: number | undefined = undefined;
        const weapon = scene.player.data?.equipment?.Weapon;
        if (weapon && weapon.definition.stats) {
          if (weapon.definition.stats.range) range = weapon.definition.stats.range;
          if (weapon.definition.stats.accuracy !== undefined)
            accuracy = weapon.definition.stats.accuracy;
        } else {
          range = PLAYER_UNARMED_ATTACK_RANGE;
          accuracy = 100;
          spreadAngle = Math.PI / 2;
        }
        attackFieldGraphics = drawAttackField(
          scene,
          scene.player.x,
          scene.player.y + WEAPON_Y_OFFSET,
          { x: currentAttackDirection.dx, y: currentAttackDirection.dy },
          range,
          accuracy,
          0xff0000,
          0.1,
          spreadAngle,
        );
      }
    }

    if (
      scene.player &&
      scene.player.attackCooldownBar &&
      scene.player.socketId === (window as any).player?.socketId
    ) {
      // Get current attack interval
      let attackIntervalMs = PLAYER_UNARMED_ATTACK_INTERVAL;
      if (
        window.player &&
        window.player.data &&
        window.player.data.equipment &&
        window.player.data.equipment.Weapon &&
        window.player.data.equipment.Weapon.definition.stats &&
        window.player.data.equipment.Weapon.definition.stats.attackInterval
      ) {
        attackIntervalMs =
          window.player.data.equipment.Weapon.definition.stats.attackInterval;
      }
      const now = Date.now();
      const timeSinceLastAttack = now - lastAttackTime;
      const progress = Math.min(timeSinceLastAttack / attackIntervalMs, 1);
      scene.player.attackCooldownBar.setProgress(progress);
    }
  });

  // Stamina management
  let stamina = StaminaMeter.STAMINA_MAX;
  let isSprinting = false;

  // Create stamina meter
  const staminaMeterX = gameWidth / 2;
  const staminaMeterY = scene.scale.height - 20;
  const staminaMeter = new StaminaMeter(
    scene,
    staminaMeterX,
    staminaMeterY,
    uiLayer,
  );

  // Create emoji button to the right of the sprint button
  const emojiButtonX = rightJoystickX + 80;
  const emojiButtonY = rightJoystickY + 80;
  const emojiBtn = scene.add.image(emojiButtonX, emojiButtonY, "emoticons-icon");
  emojiBtn.setDisplaySize(34, 34);
  emojiBtn.setScrollFactor(0);
  emojiBtn.setDepth(10099);
  emojiBtn.setVisible(!localOnly);
  emojiBtn.setInteractive({ useHandCursor: true });
  emojiBtn.on("pointerdown", () => {
    if (currentEmojiPicker && currentEmojiPicker.isActive()) {
      currentEmojiPicker.destroy();
      currentEmojiPicker = null;
      return;
    }
    currentEmojiPicker = new EmojiPicker(
      scene,
      emojiButtonX,
      emojiButtonY - 4,
      uiLayer,
      (selection) => {
        currentEmojiPicker = null;
        if (!window.socket || !scene.player) return;
        if (selection.kind === "emote") {
          window.socket.emit("player-emoji", { emoji: selection.key });
        } else {
          scene.enterStickerPlacementMode?.(selection.sprayId);
        }
      },
    );
  });
  globalEmojiButton = emojiBtn;
  if (uiLayer) {
    uiLayer.add(globalEmojiButton);
  }

  // Create Sprint button with progress bar below the right joystick
  if (showSprint) {
    globalSprintButton = new SprintButton(
      scene,
      rightJoystickX,
      rightJoystickY + 80, // Position below the right joystick
      () => {
        // Sprint button clicked - send sprint action to server
        if (scene.player && scene.player.isAlive()) {
          (window as any).socket.emit("player_sprint", {
            sprinterId: scene.player.socketId,
          });
        }
      },
      uiLayer,
      () => {
        // Sprint hold started
        if (stamina > 0) {
          isSprinting = true;
          attackJoystick.setVisible(false);
          // Meter visibility is controlled by stamina value in setStamina
        }
      },
      () => {
        // Sprint hold ended
        isSprinting = false;
        attackJoystick.setVisible(true);
        // Meter visibility is controlled by stamina value in setStamina
      },
    );
  } else {
    globalSprintButton = null;
  }

  // Store sprint button on scene for cleanup and updates
  scene.sprintButton = globalSprintButton;
  scene.staminaMeter = staminaMeter;

  // Keep our own reference so cleanup never touches another scene's sprint button
  const ownSprintButton = globalSprintButton;

  // Store joysticks on scene for cleanup
  scene.movementJoystick = movementJoystick;
  scene.attackJoystick = attackJoystick;

  // Store global references for external control
  globalMovementJoystick = movementJoystick;
  globalAttackJoystick = attackJoystick;

  // --- Stamina update loop ---
  const staminaUpdateHandler = (time: number, delta: number) => {
    if (!scene.player || !scene.player.isAlive()) return;

    // Check if sprinting and moving
    const isSprintingAndMoving = isMarketScene
      ? movementJoystick.force > 5
      : isSprinting &&
        globalSprintButton?.isPressed() &&
        movementJoystick.force > 5 &&
        stamina > 0;

    if (isSprintingAndMoving) {
      // Deplete stamina (skip if admin mode is active, or in the market scene where sprint is free)
      if (!scene.player.adminMode && !isMarketScene) {
        stamina = Math.max(
          0,
          stamina - StaminaMeter.STAMINA_DEPLETION_RATE * delta,
        );
      }

      // If stamina reaches 0, stop sprinting
      if (!isMarketScene && stamina <= 0) {
        stamina = 0;
        isSprinting = false;
        attackJoystick.setVisible(true);
        staminaMeter.hide();
      }
    } else if (!globalSprintButton?.isPressed()) {
      // Replenish stamina when not sprinting
      stamina = Math.min(
        StaminaMeter.STAMINA_MAX,
        stamina + StaminaMeter.STAMINA_REPLENISH_RATE * delta,
      );
    }

    // Update stamina meter display (always update, not just when sprinting)
    if (staminaMeter) {
      staminaMeter.setStamina(stamina);
    }
  };
  scene.events.on("update", staminaUpdateHandler);

  // Function to update joystick visibility based on window state
  const updateJoystickVisibility = () => {
    // Guard check: if scene is shutting down or joysticks are invalid, don't update
    if (!scene.isActive || !movementJoystick || !attackJoystick) {
      return;
    }

    let windowsOpen = false;

    // Check WindowManager for all managed windows
    if (scene.windowManager && scene.windowManager.isAnyWindowOpen()) {
      windowsOpen = true;
    }

    // Check death screen (not managed by WindowManager)
    if (
      scene.deathScreen &&
      scene.deathScreen.container &&
      scene.deathScreen.container.visible
    ) {
      windowsOpen = true;
    }

    movementJoystick.setVisible(!windowsOpen);
    // Hide attack joystick if windows are open or if sprint is active
    attackJoystick.setVisible(!windowsOpen && !globalSprintButton?.isPressed());
    globalSprintButton?.setVisible(!windowsOpen && !localOnly);
    globalEmojiButton?.setVisible(!windowsOpen && !localOnly);
  };

  // Set up periodic check for window visibility (only on mobile)
  let visibilityCheckInterval: number | null = null;
  if (isTouchDevice()) {
    // Initial check
    updateJoystickVisibility();

    // Check every 100ms for window changes
    visibilityCheckInterval = window.setInterval(() => {
      updateJoystickVisibility();
    }, 100);
  }

  // Cleanup function
  // NOTE: Phaser only emits "destroy" when a scene is removed from the SceneManager.
  // Switching scenes (scene.start/scene.stop) emits "shutdown" and keeps the scene
  // instance (and its event listeners) alive, so cleanup must run on both events.
  // Otherwise the movement interval survives with isMoving === true and keeps walking
  // the player the next time the same scene is created (e.g. holding the movement
  // joystick while pressing EXIT in the market).
  let isCleanedUp = false;
  const cleanup = () => {
    if (isCleanedUp) {
      return;
    }
    isCleanedUp = true;

    // Reset movement/attack state so a held joystick can't resume after a scene switch
    isMoving = false;
    currentMovement.dx = 0;
    currentMovement.dy = 0;
    isAttacking = false;
    isAiming = false;
    hasAimed = false;

    // Clean up intervals
    if (movementInterval) {
      clearInterval(movementInterval);
      movementInterval = null;
    }
    if (attackCheckInterval) {
      clearInterval(attackCheckInterval);
      attackCheckInterval = null;
    }
    if (visibilityCheckInterval) {
      clearInterval(visibilityCheckInterval);
      visibilityCheckInterval = null;
    }

    // Stop listening for scene updates (scene events survive a shutdown)
    scene.events.off("update", staminaUpdateHandler);

    // Destroy joysticks
    if (movementJoystick) {
      movementJoystick.destroy();
    }
    if (attackJoystick) {
      attackJoystick.destroy();
    }

    // Destroy sprint button and stamina meter
    if (ownSprintButton) {
      ownSprintButton.destroy();
    }
    if (staminaMeter) {
      staminaMeter.destroy();
    }

    // Destroy emoji button and picker
    if (currentEmojiPicker) {
      currentEmojiPicker.destroy();
      currentEmojiPicker = null;
    }
    if (globalEmojiButton) {
      globalEmojiButton.destroy();
      globalEmojiButton = null;
    }

    // Clear scene references so a recreated scene never reuses stale controls
    if (scene.movementJoystick === movementJoystick) {
      scene.movementJoystick = null;
    }
    if (scene.attackJoystick === attackJoystick) {
      scene.attackJoystick = null;
    }
    if (scene.sprintButton === ownSprintButton) {
      scene.sprintButton = null;
    }
    if (scene.staminaMeter === staminaMeter) {
      scene.staminaMeter = null;
    }

    // Clear global references
    if (globalMovementJoystick === movementJoystick) {
      globalMovementJoystick = null;
    }
    if (globalAttackJoystick === attackJoystick) {
      globalAttackJoystick = null;
    }
    if (globalSprintButton === ownSprintButton) {
      globalSprintButton = null;
    }
  };

  scene.events.once("shutdown", cleanup);
  scene.events.once("destroy", cleanup);
}

// Export functions to control joystick visibility and throwable mode from other modules
export function hideJoysticks() {
  if (globalMovementJoystick) {
    globalMovementJoystick.setVisible(false);
  }
  if (globalAttackJoystick) {
    globalAttackJoystick.setVisible(false);
  }
  globalSprintButton?.setVisible(false);
  globalEmojiButton?.setVisible(false);
}

export function showJoysticks() {
  if (globalMovementJoystick) {
    globalMovementJoystick.setVisible(true);
  }
  if (globalAttackJoystick) {
    globalAttackJoystick.setVisible(true);
  }
  globalSprintButton?.setVisible(true);
  globalEmojiButton?.setVisible(!globalLocalOnly);
}

export function setThrowableMode(enabled: boolean) {
  if (globalAttackJoystick) {
    globalAttackJoystick.setThrowableMode(enabled);
  }
}
